import type { BridgeId, VesselCorridor } from "./contracts";
import {
  BRIDGE_GEOGRAPHIC_POINTS,
  BRIDGE_S_METERS,
  geographicDistanceMeters,
  RIVER_BRICKELL_GEOGRAPHIC_POINT,
  RIVER_MOUTH_S_METERS,
  RIVER_OUTER_S_METERS,
  type GeographicPoint,
} from "./river-geography";
export { GEOGRAPHIC_ROUTES, RIVER_BRICKELL_GEOGRAPHIC_POINT, RIVER_MOUTH_S_METERS, RIVER_OUTER_S_METERS } from "./river-geography";

export interface RiverPoint {
  x: number;
  z: number;
}

export interface DiagramPoint {
  x: number;
  y: number;
}

export interface BridgeStation {
  id: BridgeId;
  name: string;
  shortName: string;
  sMeters: number;
  scene: RiverPoint;
  diagram: DiagramPoint;
}

export interface RouteLocation {
  point: RiverPoint;
  tangent: RiverPoint;
  route: VesselCorridor;
}

interface RouteKnot {
  key: string;
  point: RiverPoint;
  measure: number;
}

interface DiagramKnot {
  point: DiagramPoint;
  measure: number;
}

interface CubicBezierSegment {
  start: RouteKnot;
  end: RouteKnot;
  p0: RiverPoint;
  p1: RiverPoint;
  p2: RiverPoint;
  p3: RiverPoint;
  arc: readonly ArcSample[];
  length: number;
  linear?: boolean;
}

interface ArcSample {
  t: number;
  distance: number;
}

export const CORRIDOR_VIEWBOX = { width: 1320, height: 640 } as const;

// OSM waterway ways 913873352, 83150142, 83150152, and 161883304,
// ordered from the authored river mouth through the Miami River, North Fork,
// and Miami Canal. The denser navigation spine keeps bounded visual estimates
// inside the mapped water instead of cutting chords across river bends.
const MIAMI_RIVER_NAVIGATION_CENTERLINE: readonly GeographicPoint[] = [
  [25.771, -80.1849],
  [25.7704473, -80.1872888],
  [25.7701504, -80.1882249],
  [25.769991, -80.1890296],
  [25.7698895, -80.1897538],
  RIVER_BRICKELL_GEOGRAPHIC_POINT,
  [25.7698992, -80.1903224],
  [25.7700103, -80.1907998],
  [25.7703146, -80.1916903],
  [25.7703726, -80.1920175],
  [25.7703388, -80.1923662],
  [25.7701745, -80.1928007],
  [25.7694837, -80.193761],
  [25.7691311, -80.1942867],
  [25.768875, -80.1949626],
  [25.7686866, -80.1959014],
  [25.7686431, -80.1963735],
  [25.7687059, -80.1969045],
  [25.7688315, -80.1974088],
  [25.7690779, -80.1978969],
  [25.769503, -80.1983905],
  [25.7707639, -80.1996028],
  [25.7710779, -80.1998603],
  [25.7714209, -80.2000159],
  [25.7721842, -80.2001768],
  [25.7725948, -80.2003002],
  [25.7733726, -80.2008045],
  [25.7746237, -80.2015448],
  [25.7748991, -80.2017808],
  [25.7753821, -80.2024406],
  [25.7757879, -80.2028751],
  [25.7767685, -80.2037871],
  [25.7770874, -80.2041304],
  [25.7772516, -80.2044523],
  [25.7775221, -80.2053052],
  [25.777812, -80.2060455],
  [25.7781066, -80.206598],
  [25.7784882, -80.2071184],
  [25.7787781, -80.207628],
  [25.7792032, -80.2085239],
  [25.7797007, -80.2093929],
  [25.7800968, -80.2098971],
  [25.7805654, -80.2103317],
  [25.7810436, -80.2109164],
  [25.7814638, -80.2116728],
  [25.7818986, -80.2126545],
  [25.7828646, -80.2154279],
  [25.7841882, -80.2193385],
  [25.785048, -80.2219403],
  [25.7852267, -80.2223426],
  [25.7856172, -80.2230776],
  [25.7857967, -80.2234155],
  [25.7860188, -80.2238339],
  [25.7861493, -80.2242309],
  [25.7861879, -80.2246708],
  [25.7861154, -80.225631],
  [25.7861348, -80.2260333],
  [25.7863521, -80.2266073],
  [25.786782, -80.227471],
  [25.7869511, -80.2279323],
  [25.7872892, -80.2291286],
  [25.7874244, -80.229429],
  [25.7875887, -80.2296597],
  [25.7880862, -80.2301478],
  [25.7884001, -80.2304965],
  [25.7886658, -80.2309793],
  [25.7891585, -80.2322131],
  [25.7895304, -80.2330822],
  [25.7899603, -80.2340048],
  [25.7900762, -80.2344072],
  [25.7902742, -80.235362],
  [25.7903708, -80.2357697],
  [25.7905544, -80.2361721],
  [25.7914576, -80.2376205],
  [25.7935297, -80.2411234],
  [25.7953708, -80.2442209],
  [25.7960074, -80.2451843],
  [25.799422, -80.2496475],
  [25.8048119, -80.2569377],
  [25.8074584, -80.2604971],
];

const SCENE_FRAME = sceneFrame();

const STATION_DEFINITIONS = [
  ["brickell", "Brickell Avenue", "Brickell", BRIDGE_S_METERS["brickell"], { x: 0, z: 0 }, { x: 750, y: 320 }],
  ["sw-2-ave", "Southwest 2nd Avenue", "SW 2 Ave", BRIDGE_S_METERS["sw-2-ave"], { x: -31.407, z: -2.727 }, { x: 555, y: 320 }],
  ["sw-1-st", "Southwest 1st Street", "SW 1 St", BRIDGE_S_METERS["sw-1-st"], { x: -39.277, z: -24.208 }, { x: 485, y: 260 }],
  ["w-flagler", "West Flagler Street", "W Flagler", BRIDGE_S_METERS["w-flagler"], { x: -40.896, z: -30.103 }, { x: 405, y: 260 }],
  ["nw-5-st", "Northwest 5th Street", "NW 5 St", BRIDGE_S_METERS["nw-5-st"], { x: -59.193, z: -54.044 }, { x: 335, y: 200 }],
  ["nw-12-ave", "Northwest 12th Avenue", "NW 12 Ave", BRIDGE_S_METERS["nw-12-ave"], { x: -86.181, z: -80.749 }, { x: 255, y: 200 }],
  ["nw-17-ave", "Northwest 17th Avenue", "NW 17 Ave", BRIDGE_S_METERS["nw-17-ave"], { x: -116.983, z: -102.411 }, { x: 195, y: 140 }],
  ["nw-22-ave", "Northwest 22nd Avenue", "NW 22 Ave", BRIDGE_S_METERS["nw-22-ave"], { x: -146.043, z: -123.918 }, { x: 130, y: 140 }],
  ["nw-27-ave", "Northwest 27th Avenue", "NW 27 Ave", BRIDGE_S_METERS["nw-27-ave"], { x: -176.378, z: -149.806 }, { x: 75, y: 80 }],
] as const satisfies readonly [BridgeId, string, string, number, RiverPoint, DiagramPoint][];

const trunkKnots: readonly RouteKnot[] = navigationTrunkKnots();

const branchKnots: Readonly<Record<Exclude<VesselCorridor, "miami_river">, readonly RouteKnot[]>> = {
  north_approach: [
    { key: "mouth", measure: 0, point: { x: 22, z: 0 } },
    { key: "north-inner", measure: 0.27, point: { x: 62, z: -12 } },
    { key: "north-mid", measure: 0.62, point: { x: 108, z: -25 } },
    { key: "north-outer", measure: 1, point: { x: 164, z: -43 } },
  ],
  government_cut: [
    { key: "mouth", measure: 0, point: { x: 22, z: 0 } },
    { key: "cut-inner", measure: 0.3, point: { x: 70, z: -1 } },
    { key: "cut-mid", measure: 0.68, point: { x: 128, z: 1 } },
    { key: "cut-outer", measure: 1, point: { x: 184, z: 4 } },
  ],
  south_approach: [
    { key: "mouth", measure: 0, point: { x: 22, z: 0 } },
    { key: "south-inner", measure: 0.28, point: { x: 60, z: 14 } },
    { key: "south-mid", measure: 0.63, point: { x: 106, z: 28 } },
    { key: "south-outer", measure: 1, point: { x: 158, z: 48 } },
  ],
};

// The flat atlas is a legibility-first cartographic projection of the same
// river model. Physical scene anchors are intentionally not passed through a
// single affine transform: the closely spaced downtown spans need authored
// screen distance, and the oversized Brickell symbol needs two reserved
// portals. Measures still bind every diagram knot to the canonical route.
const trunkDiagramKnots: readonly DiagramKnot[] = [
  { measure: RIVER_MOUTH_S_METERS, point: { x: 1_000, y: 320 } },
  { measure: -60, point: { x: 910, y: 320 } },
  { measure: 0, point: STATION_DEFINITIONS[0][5] },
  { measure: 60, point: { x: 565, y: 320 } },
  ...STATION_DEFINITIONS.slice(1).map((definition) => ({
    measure: definition[3],
    point: definition[5],
  })),
  { measure: RIVER_OUTER_S_METERS, point: { x: 30, y: 80 } },
];

const branchDiagramKnots: Readonly<Record<Exclude<VesselCorridor, "miami_river">, readonly DiagramKnot[]>> = {
  north_approach: [
    { measure: 0, point: { x: 1_000, y: 320 } },
    { measure: 0.28, point: { x: 1_060, y: 250 } },
    { measure: 0.63, point: { x: 1_160, y: 250 } },
    { measure: 1, point: { x: 1_280, y: 130 } },
  ],
  government_cut: [
    { measure: 0, point: { x: 1_000, y: 320 } },
    { measure: 0.4, point: { x: 1_100, y: 320 } },
    { measure: 1, point: { x: 1_280, y: 320 } },
  ],
  south_approach: [
    { measure: 0, point: { x: 1_000, y: 320 } },
    { measure: 0.28, point: { x: 1_060, y: 390 } },
    { measure: 0.63, point: { x: 1_160, y: 390 } },
    { measure: 1, point: { x: 1_280, y: 510 } },
  ],
};

const trunkSegments = linearSegments(trunkKnots);
const branchSegments = Object.fromEntries(
  Object.entries(branchKnots).map(([route, knots]) => [route, smoothSegments(knots)]),
) as Record<Exclude<VesselCorridor, "miami_river">, readonly CubicBezierSegment[]>;

export const BRIDGE_STATIONS: readonly BridgeStation[] = STATION_DEFINITIONS.map(
  ([id, name, shortName, sMeters, scene, diagram]) => ({
    id,
    name,
    shortName,
    sMeters,
    scene,
    diagram,
  }),
);

function navigationTrunkKnots(): RouteKnot[] {
  const routeSamples = cumulativeGeographicSamples(MIAMI_RIVER_NAVIGATION_CENTERLINE);
  const calibration = [
    {
      key: "mouth",
      arcMeters: 0,
      measure: RIVER_MOUTH_S_METERS,
      point: geographicToScene(MIAMI_RIVER_NAVIGATION_CENTERLINE[0]!),
    },
    ...STATION_DEFINITIONS.map((station) => {
      const arcMeters = nearestGeographicArcMeters(BRIDGE_GEOGRAPHIC_POINTS[station[0]], routeSamples);
      return {
        key: station[0],
        arcMeters,
        measure: station[3],
        point: station[4],
      };
    }),
    {
      key: "river-outer",
      arcMeters: routeSamples.at(-1)?.arcMeters ?? 0,
      measure: RIVER_OUTER_S_METERS,
      point: geographicToScene(MIAMI_RIVER_NAVIGATION_CENTERLINE.at(-1)!),
    },
  ].sort((left, right) => left.arcMeters - right.arcMeters);

  const centerlineKnots = routeSamples
    .filter((sample) => calibration.every((anchor) =>
      Math.abs(anchor.arcMeters - sample.arcMeters) > 8))
    .map((sample, index) => ({
      key: `navigation-${index}`,
      arcMeters: sample.arcMeters,
      measure: calibratedRouteMeasure(sample.arcMeters, calibration),
      point: geographicToScene(sample.point),
    }));

  return [...centerlineKnots, ...calibration]
    .sort((left, right) => left.arcMeters - right.arcMeters)
    .map(({ key, measure, point }) => ({ key, measure, point }));
}

interface GeographicRouteSample {
  point: GeographicPoint;
  arcMeters: number;
}

function cumulativeGeographicSamples(
  points: readonly GeographicPoint[],
): GeographicRouteSample[] {
  let arcMeters = 0;
  return points.map((point, index) => {
    if (index > 0) arcMeters += geographicDistanceMeters(points[index - 1]!, point);
    return { point, arcMeters };
  });
}

function nearestGeographicArcMeters(
  point: GeographicPoint,
  samples: readonly GeographicRouteSample[],
): number {
  const metersPerLongitude = 111_320 * Math.cos((point[0] * Math.PI) / 180);
  let bestArc = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < samples.length - 1; index += 1) {
    const start = samples[index]!;
    const end = samples[index + 1]!;
    const ax = (start.point[1] - point[1]) * metersPerLongitude;
    const az = (start.point[0] - point[0]) * 111_320;
    const bx = (end.point[1] - point[1]) * metersPerLongitude;
    const bz = (end.point[0] - point[0]) * 111_320;
    const dx = bx - ax;
    const dz = bz - az;
    const lengthSquared = dx * dx + dz * dz;
    const ratio = lengthSquared === 0
      ? 0
      : bounded(-(ax * dx + az * dz) / lengthSquared, 0, 1);
    const projectedX = ax + ratio * dx;
    const projectedZ = az + ratio * dz;
    const distance = Math.hypot(projectedX, projectedZ);
    if (distance >= bestDistance) continue;
    bestDistance = distance;
    bestArc = start.arcMeters + ratio * (end.arcMeters - start.arcMeters);
  }
  return bestArc;
}

function calibratedRouteMeasure(
  arcMeters: number,
  anchors: readonly { arcMeters: number; measure: number }[],
): number {
  const end = anchors.find((anchor) => anchor.arcMeters >= arcMeters) ?? anchors.at(-1)!;
  const endIndex = anchors.indexOf(end);
  const start = anchors[Math.max(0, endIndex - 1)] ?? end;
  const span = end.arcMeters - start.arcMeters;
  const ratio = span <= 0 ? 0 : bounded((arcMeters - start.arcMeters) / span, 0, 1);
  return start.measure + (end.measure - start.measure) * ratio;
}

interface SceneFrame {
  brickell: GeographicPoint;
  metersPerLatitude: number;
  metersPerLongitude: number;
  mouthEast: number;
  mouthNorth: number;
  mouthMeters: number;
}

/**
 * The local tangent-plane frame shared by every scene projection: +x runs
 * from Brickell toward the authored river mouth, +z is that axis rotated
 * clockwise when viewed from above (so -z is roughly north).
 */
function sceneFrame(): SceneFrame {
  const brickell = RIVER_BRICKELL_GEOGRAPHIC_POINT;
  const mouth = MIAMI_RIVER_NAVIGATION_CENTERLINE[0]!;
  const metersPerLatitude = 111_320;
  const metersPerLongitude = metersPerLatitude * Math.cos((brickell[0] * Math.PI) / 180);
  const mouthEast = (mouth[1] - brickell[1]) * metersPerLongitude;
  const mouthNorth = (mouth[0] - brickell[0]) * metersPerLatitude;
  return {
    brickell,
    metersPerLatitude,
    metersPerLongitude,
    mouthEast,
    mouthNorth,
    mouthMeters: Math.hypot(mouthEast, mouthNorth),
  };
}

function geographicToScene(point: GeographicPoint): RiverPoint {
  const frame = SCENE_FRAME;
  const east = (point[1] - frame.brickell[1]) * frame.metersPerLongitude;
  const north = (point[0] - frame.brickell[0]) * frame.metersPerLatitude;
  const along = (east * frame.mouthEast + north * frame.mouthNorth) / frame.mouthMeters;
  const perpendicular = (-east * frame.mouthNorth + north * frame.mouthEast) / frame.mouthMeters;
  return {
    x: (along / frame.mouthMeters) * 22,
    z: (-perpendicular / frame.mouthMeters) * 22,
  };
}

/**
 * Unit scene vector for a reported compass course over ground, in the same
 * frame as every authored route point. Returns null for absent or invalid
 * courses so callers fall back to route evidence.
 */
export function courseSceneVector(courseDegrees: number | null | undefined): RiverPoint | null {
  if (courseDegrees === null || courseDegrees === undefined || !Number.isFinite(courseDegrees)) return null;
  const radians = (courseDegrees * Math.PI) / 180;
  const east = Math.sin(radians);
  const north = Math.cos(radians);
  const frame = SCENE_FRAME;
  const along = (east * frame.mouthEast + north * frame.mouthNorth) / frame.mouthMeters;
  const perpendicular = (-east * frame.mouthNorth + north * frame.mouthEast) / frame.mouthMeters;
  return normalize({ x: along, z: -perpendicular });
}

/** Compass course over ground, in degrees, for a scene direction vector. */
export function sceneVectorCourseDegrees(vector: RiverPoint): number {
  const frame = SCENE_FRAME;
  const unitEast = frame.mouthEast / frame.mouthMeters;
  const unitNorth = frame.mouthNorth / frame.mouthMeters;
  const along = vector.x;
  const perpendicular = -vector.z;
  const east = along * unitEast - perpendicular * unitNorth;
  const north = along * unitNorth + perpendicular * unitEast;
  const degrees = (Math.atan2(east, north) * 180) / Math.PI;
  return (degrees + 360) % 360;
}

/**
 * The route tangent pointing toward increasing s: from the bay toward the
 * river mouth, then upriver past Brickell. Approach branches are authored
 * from the mouth outward, so their raw tangent is reversed here.
 */
export function inboundRouteTangent(location: RouteLocation): RiverPoint {
  return location.route === "miami_river"
    ? location.tangent
    : { x: -location.tangent.x, z: -location.tangent.z };
}

export function bridgeStation(id: BridgeId): BridgeStation {
  const station = BRIDGE_STATIONS.find((candidate) => candidate.id === id);
  if (!station) throw new Error(`Unknown bridge: ${id}`);
  return station;
}

export function routeLocation(
  corridor: VesselCorridor,
  sMeters: number,
  corridorProgress: number,
): RouteLocation {
  if (corridor === "miami_river" || sMeters >= RIVER_MOUTH_S_METERS) {
    return locationOnSegments(
      trunkSegments,
      bounded(Number.isFinite(sMeters) ? sMeters : 0, RIVER_MOUTH_S_METERS, RIVER_OUTER_S_METERS),
      "miami_river",
    );
  }
  return locationOnSegments(
    branchSegments[corridor],
    bounded(Number.isFinite(corridorProgress) ? corridorProgress : 0, 0, 1),
    corridor,
  );
}

export function routePointAtSMeters(sMeters: number): RiverPoint {
  return routeLocation("miami_river", sMeters, 0).point;
}

export function routeTangentAtSMeters(sMeters: number): RiverPoint {
  return routeLocation("miami_river", sMeters, 0).tangent;
}

export function routeDiagramPoint(
  corridor: VesselCorridor,
  sMeters: number,
  corridorProgress: number,
): DiagramPoint {
  if (corridor === "miami_river" || sMeters >= RIVER_MOUTH_S_METERS) {
    return interpolateDiagramKnots(
      trunkDiagramKnots,
      bounded(Number.isFinite(sMeters) ? sMeters : 0, RIVER_MOUTH_S_METERS, RIVER_OUTER_S_METERS),
    );
  }
  return interpolateDiagramKnots(
    branchDiagramKnots[corridor],
    bounded(Number.isFinite(corridorProgress) ? corridorProgress : 0, 0, 1),
  );
}

export function sampledRoute(
  corridor: VesselCorridor,
  samples = corridor === "miami_river" ? 96 : 36,
): readonly RiverPoint[] {
  const points: RiverPoint[] = [];
  for (let index = 0; index <= samples; index += 1) {
    const ratio = index / samples;
    points.push(corridor === "miami_river"
      ? routeLocation(corridor, RIVER_MOUTH_S_METERS + ratio * (RIVER_OUTER_S_METERS - RIVER_MOUTH_S_METERS), 0).point
      : routeLocation(corridor, RIVER_MOUTH_S_METERS - ratio, ratio).point);
  }
  return points;
}

export function svgRoutePath(corridor: VesselCorridor): string {
  const knots = corridor === "miami_river" ? trunkDiagramKnots : branchDiagramKnots[corridor];
  return knots
    .map(({ point }, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(" ");
}

function interpolateDiagramKnots(knots: readonly DiagramKnot[], measure: number): DiagramPoint {
  const first = knots[0] ?? { measure: 0, point: { x: 0, y: 0 } };
  const last = knots.at(-1) ?? first;
  if (measure <= first.measure) return { ...first.point };
  if (measure >= last.measure) return { ...last.point };

  for (let index = 0; index < knots.length - 1; index += 1) {
    const start = knots[index] ?? first;
    const end = knots[index + 1] ?? start;
    if (measure > end.measure) continue;
    const ratio = (measure - start.measure) / (end.measure - start.measure || 1);
    return {
      x: start.point.x + (end.point.x - start.point.x) * ratio,
      y: start.point.y + (end.point.y - start.point.y) * ratio,
    };
  }
  return { ...last.point };
}

function smoothSegments(knots: readonly RouteKnot[]): readonly CubicBezierSegment[] {
  return knots.slice(0, -1).map((start, index) => {
    const end = knots[index + 1]!;
    const before = knots[index - 1]?.point ?? start.point;
    const after = knots[index + 2]?.point ?? end.point;
    const p0 = start.point;
    const p1 = add(p0, scale(subtract(end.point, before), 1 / 6));
    const p2 = subtract(end.point, scale(subtract(after, start.point), 1 / 6));
    const p3 = end.point;
    const arc = buildArcLookup(p0, p1, p2, p3);
    return { start, end, p0, p1, p2, p3, arc, length: arc.at(-1)?.distance ?? 0 };
  });
}

function linearSegments(knots: readonly RouteKnot[]): readonly CubicBezierSegment[] {
  return knots.slice(0, -1).map((start, index) => {
    const end = knots[index + 1]!;
    const p0 = start.point;
    const p3 = end.point;
    const delta = subtract(p3, p0);
    const p1 = add(p0, scale(delta, 1 / 3));
    const p2 = add(p0, scale(delta, 2 / 3));
    const arc = buildArcLookup(p0, p1, p2, p3);
    return {
      start,
      end,
      p0,
      p1,
      p2,
      p3,
      arc,
      length: arc.at(-1)?.distance ?? 0,
      linear: true,
    };
  });
}

function buildArcLookup(p0: RiverPoint, p1: RiverPoint, p2: RiverPoint, p3: RiverPoint): ArcSample[] {
  const lookup: ArcSample[] = [{ t: 0, distance: 0 }];
  let previous = p0;
  let distance = 0;
  for (let index = 1; index <= 32; index += 1) {
    const t = index / 32;
    const point = cubicPoint(p0, p1, p2, p3, t);
    distance += Math.hypot(point.x - previous.x, point.z - previous.z);
    lookup.push({ t, distance });
    previous = point;
  }
  return lookup;
}

function locationOnSegments(
  segments: readonly CubicBezierSegment[],
  measure: number,
  route: VesselCorridor,
): RouteLocation {
  const segment = segments.find((candidate) => measure <= candidate.end.measure) ?? segments.at(-1);
  if (!segment) return { point: { x: 0, z: 0 }, tangent: { x: 1, z: 0 }, route };
  const measureSpan = segment.end.measure - segment.start.measure || 1;
  const distanceRatio = bounded((measure - segment.start.measure) / measureSpan, 0, 1);
  const t = parameterAtArcRatio(segment, distanceRatio);
  const tangent = segment.linear
    ? blendedPolylineTangent(segments, segments.indexOf(segment), distanceRatio)
    : normalize(cubicDerivative(segment.p0, segment.p1, segment.p2, segment.p3, t));
  return { point: cubicPoint(segment.p0, segment.p1, segment.p2, segment.p3, t), tangent, route };
}

function blendedPolylineTangent(
  segments: readonly CubicBezierSegment[],
  index: number,
  ratio: number,
): RiverPoint {
  const current = segmentDirection(segments[index]!);
  const previous = index > 0 ? segmentDirection(segments[index - 1]!) : current;
  const next = index < segments.length - 1 ? segmentDirection(segments[index + 1]!) : current;
  const start = normalize(add(previous, current));
  const end = normalize(add(current, next));
  return normalize({
    x: start.x + (end.x - start.x) * ratio,
    z: start.z + (end.z - start.z) * ratio,
  });
}

function segmentDirection(segment: CubicBezierSegment): RiverPoint {
  return normalize(subtract(segment.p3, segment.p0));
}

function parameterAtArcRatio(segment: CubicBezierSegment, ratio: number): number {
  const target = ratio * segment.length;
  const upperIndex = segment.arc.findIndex((sample) => sample.distance >= target);
  if (upperIndex <= 0) return upperIndex < 0 ? 1 : 0;
  const lower = segment.arc[upperIndex - 1]!;
  const upper = segment.arc[upperIndex]!;
  const span = upper.distance - lower.distance || 1;
  return lower.t + ((target - lower.distance) / span) * (upper.t - lower.t);
}

function cubicPoint(
  p0: RiverPoint,
  p1: RiverPoint,
  p2: RiverPoint,
  p3: RiverPoint,
  t: number,
): RiverPoint {
  const oneMinus = 1 - t;
  return {
    x: oneMinus ** 3 * p0.x + 3 * oneMinus ** 2 * t * p1.x + 3 * oneMinus * t ** 2 * p2.x + t ** 3 * p3.x,
    z: oneMinus ** 3 * p0.z + 3 * oneMinus ** 2 * t * p1.z + 3 * oneMinus * t ** 2 * p2.z + t ** 3 * p3.z,
  };
}

function cubicDerivative(
  p0: RiverPoint,
  p1: RiverPoint,
  p2: RiverPoint,
  p3: RiverPoint,
  t: number,
): RiverPoint {
  const oneMinus = 1 - t;
  return {
    x: 3 * oneMinus ** 2 * (p1.x - p0.x) + 6 * oneMinus * t * (p2.x - p1.x) + 3 * t ** 2 * (p3.x - p2.x),
    z: 3 * oneMinus ** 2 * (p1.z - p0.z) + 6 * oneMinus * t * (p2.z - p1.z) + 3 * t ** 2 * (p3.z - p2.z),
  };
}

function add(left: RiverPoint, right: RiverPoint): RiverPoint {
  return { x: left.x + right.x, z: left.z + right.z };
}

function subtract(left: RiverPoint, right: RiverPoint): RiverPoint {
  return { x: left.x - right.x, z: left.z - right.z };
}

function scale(point: RiverPoint, amount: number): RiverPoint {
  return { x: point.x * amount, z: point.z * amount };
}

function normalize(point: RiverPoint): RiverPoint {
  const length = Math.hypot(point.x, point.z) || 1;
  return { x: point.x / length, z: point.z / length };
}

function bounded(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
